import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { formatBytes, formatNumber } from '../../lib/format';
import { AdminPage, QueryState } from './AdminLayout';
import { MiniBarChart } from './MiniBarChart';
import { OnlineNow } from './OnlineNow';

export function AdminOverview() {
  const { t } = useTranslation();
  const stats = useQuery({
    queryKey: ['admin', 'stats'],
    queryFn: api.admin.stats,
    refetchInterval: 60_000,
  });
  if (!stats.data) {
    return (
      <AdminPage title={t('Resumen')}>
        <QueryState error={stats.error} onRetry={() => void stats.refetch()} />
      </AdminPage>
    );
  }
  const s = stats.data;
  const tiles = [
    {
      label: t('Usuarios'),
      value: s.users.total,
      detail:
        s.users.newThisWeek === 1
          ? t('1 nuevo esta semana')
          : t('{{count}} nuevos esta semana', { count: formatNumber(s.users.newThisWeek) }),
    },
    {
      label: t('Activos (7 días)'),
      value: s.users.active7d,
      detail:
        s.users.suspended === 1
          ? t('1 suspendido')
          : t('{{count}} suspendidos', { count: formatNumber(s.users.suspended) }),
    },
    {
      label: t('Equipos'),
      value: s.teams.total,
      detail:
        s.webhooks.total === 1
          ? t('1 webhook')
          : t('{{count}} webhooks', { count: formatNumber(s.webhooks.total) }),
    },
    {
      label: t('Mensajes'),
      value: s.messages.total,
      detail: t('{{count}} en 24 h', { count: formatNumber(s.messages.last24h) }),
    },
    {
      label: t('Tareas'),
      value: s.tasks.total,
      detail: t('{{open}} abiertas · {{done}} hechas', {
        open: formatNumber(s.tasks.open),
        done: formatNumber(s.tasks.done),
      }),
    },
    { label: t('Archivos'), value: s.files.count, detail: formatBytes(s.files.bytes) },
  ];
  return (
    <AdminPage title={t('Resumen')} description={t('Estado general de la plataforma.')}>
      <OnlineNow />
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
        {tiles.map((tile) => (
          <div key={tile.label} className="rounded-xl border border-border p-4">
            <p className="text-xs font-medium text-muted">{tile.label}</p>
            <p className="mt-1 text-3xl font-semibold tracking-tight tabular-nums">
              {formatNumber(tile.value)}
            </p>
            <p className="mt-1 text-xs text-subtle">{tile.detail}</p>
          </div>
        ))}
      </div>
      <div className="mt-6 grid gap-3 md:grid-cols-2">
        <MiniBarChart
          title={t('Nuevos usuarios por día')}
          data={s.daily.map((d) => ({ date: d.date, value: d.users }))}
        />
        <MiniBarChart
          title={t('Mensajes por día')}
          data={s.daily.map((d) => ({ date: d.date, value: d.messages }))}
        />
      </div>
    </AdminPage>
  );
}
